// /portal/team — admin-only company-side team management.
//
// Reads through portal_team view (RLS via the new portal_read policy
// on portal_users — admins see own-company rows). Mutations all go
// through the portal-invite edge function (service-role + own-company
// guards), never raw table writes. The function carries the rules:
//   - last-admin protection (demote / revoke)
//   - self-revoke prevention
//   - cross-company rejection
//   - threshold validation
// Surfacing those as toast errors here is all the UI does.
//
// Route guard: gated admin-only at App.tsx by the PortalAdminRoute
// wrapper. Nav item hidden for non-admins.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate, formatCurrency } from '@/lib/utils'
import { ShieldOff, Send, Loader2, AlertCircle, Save } from 'lucide-react'
import { toast } from 'sonner'

type Role = 'admin' | 'member' | 'viewer'

interface TeamRow {
  id: string
  role: Role
  first_name: string | null
  last_name: string | null
  email: string | null
  full_name: string | null
  last_login_at: string | null
  created_at: string
}

const ROLE_COPY: Record<Role, { label: string; color: string; bg: string; border: string; one_liner: string }> = {
  admin:  { label: 'Admin',  color: 'var(--accent-text)',  bg: 'var(--accent-dim)',  border: 'var(--accent-border)',  one_liner: 'Full access — approve/decline at any total, manage team and threshold.' },
  member: { label: 'Member', color: 'var(--info)',         bg: 'var(--info-dim)',    border: 'var(--info-border)',    one_liner: 'Approve/decline under the threshold; recommend above it.' },
  viewer: { label: 'Viewer', color: 'var(--ink-tertiary)', bg: 'var(--hover-bg)',    border: 'var(--border-subtle)',  one_liner: 'See jobs and visits only — no quote / invoice access.' },
}

export default function PortalTeam() {
  const { portalUser } = useAuth()
  const queryClient = useQueryClient()
  const [inviteOpen, setInviteOpen] = useState(false)

  const { data: team = [], isLoading } = useQuery({
    queryKey: ['portal-team', portalUser?.company_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portal_team')
        .select('id, role, first_name, last_name, email, full_name, last_login_at, created_at')
        .order('created_at', { ascending: true })
      if (error) throw error
      return (data ?? []) as TeamRow[]
    },
    enabled: !!portalUser?.company_id,
  })

  const { data: threshold = null } = useQuery({
    queryKey: ['portal-threshold', portalUser?.company_id],
    queryFn: async () => {
      if (!portalUser?.company_id) return null
      const { data } = await supabase
        .from('companies')
        .select('portal_approval_threshold')
        .eq('id', portalUser.company_id)
        .maybeSingle()
      return data?.portal_approval_threshold ?? null
    },
    enabled: !!portalUser?.company_id,
  })

  // Shared edge-function caller (matches ContactDetail's extractError idiom).
  const invokeFn = async (body: Record<string, unknown>): Promise<{ data: any; error: string | null }> => {
    const { data, error } = await supabase.functions.invoke<any>('portal-invite', { body })
    if (error) {
      const ctx = (error as { context?: { text?: () => Promise<string> } }).context
      let msg = error.message
      if (ctx && typeof ctx.text === 'function') {
        try {
          const text = await ctx.text()
          const parsed = JSON.parse(text)
          msg = parsed?.error ?? msg
        } catch { /* keep default */ }
      }
      return { data: null, error: msg }
    }
    return { data, error: null }
  }

  const setRoleMutation = useMutation({
    mutationFn: async (vars: { portal_user_id: string; role: Role }) => {
      const r = await invokeFn({ action: 'set_role', ...vars })
      if (r.error) throw new Error(r.error)
      return r.data
    },
    onSuccess: () => {
      toast.success('Role updated')
      queryClient.invalidateQueries({ queryKey: ['portal-team', portalUser?.company_id] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const revokeMutation = useMutation({
    mutationFn: async (portal_user_id: string) => {
      const r = await invokeFn({ action: 'revoke', portal_user_id })
      if (r.error) throw new Error(r.error)
      return r.data
    },
    onSuccess: () => {
      toast.success('Access revoked')
      queryClient.invalidateQueries({ queryKey: ['portal-team', portalUser?.company_id] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ThresholdCard threshold={threshold} onSaved={() => queryClient.invalidateQueries({ queryKey: ['portal-threshold', portalUser?.company_id] })} />

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            Team ({team.length})
          </p>
          {!inviteOpen && (
            <button type="button" onClick={() => setInviteOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <Send style={{ width: 12, height: 12 }} />
              Invite
            </button>
          )}
        </div>

        {inviteOpen && (
          <InviteForm onClose={() => setInviteOpen(false)} onInvited={() => {
            queryClient.invalidateQueries({ queryKey: ['portal-team', portalUser?.company_id] })
            setInviteOpen(false)
          }} invoke={invokeFn} />
        )}

        {isLoading ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', textAlign: 'center', padding: '24px 0' }}>Loading…</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {team.map(member => (
              <MemberRow key={member.id} member={member}
                selfId={portalUser?.id ?? ''}
                onSetRole={(role) => setRoleMutation.mutate({ portal_user_id: member.id, role })}
                onRevoke={() => {
                  if (confirm(`Revoke portal access for ${member.full_name || member.email}? They will be signed out immediately.`))
                    revokeMutation.mutate(member.id)
                }}
                pending={setRoleMutation.isPending || revokeMutation.isPending} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function MemberRow({ member, selfId, onSetRole, onRevoke, pending }: {
  member: TeamRow
  selfId: string
  onSetRole: (role: Role) => void
  onRevoke: () => void
  pending: boolean
}) {
  const cfg = ROLE_COPY[member.role]
  const isSelf = member.id === selfId
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>
            {member.full_name || member.email || '—'}
            {isSelf && <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', marginLeft: 6 }}>(you)</span>}
          </p>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
            {cfg.label}
          </span>
        </div>
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>
          {member.email}
          {member.last_login_at
            ? ` · last active ${formatRelativeDate(member.last_login_at)}`
            : ' · invited, not signed in yet'}
        </p>
      </div>
      <select value={member.role} onChange={e => onSetRole(e.target.value as Role)} disabled={pending}
        style={{ padding: '6px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 11, fontWeight: 600, color: 'var(--ink-primary)', cursor: 'pointer' }}>
        <option value="admin">Admin</option>
        <option value="member">Member</option>
        <option value="viewer">Viewer</option>
      </select>
      <button type="button" onClick={onRevoke} disabled={pending || isSelf}
        title={isSelf ? "You can't revoke your own access" : 'Revoke access'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: 'transparent', border: '1px solid var(--danger-border)', borderRadius: 7, color: 'var(--danger)', fontSize: 11, fontWeight: 600, cursor: pending || isSelf ? 'not-allowed' : 'pointer', opacity: isSelf ? 0.5 : 1 }}>
        <ShieldOff style={{ width: 11, height: 11 }} />
        Revoke
      </button>
    </div>
  )
}

function InviteForm({ onClose, onInvited, invoke }: {
  onClose: () => void
  onInvited: () => void
  invoke: (body: Record<string, unknown>) => Promise<{ data: any; error: string | null }>
}) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', role: 'member' as Role })
  const [busy, setBusy] = useState(false)
  const set = (k: keyof typeof form, v: string) => setForm(p => ({ ...p, [k]: v }))

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    if (!form.email.trim() || !form.first_name.trim()) {
      toast.error('Email and first name are required')
      return
    }
    setBusy(true)
    const r = await invoke({
      action: 'invite',
      email: form.email.trim(),
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim() || undefined,
      role: form.role,
    })
    setBusy(false)
    if (r.error) { toast.error(r.error); return }
    toast.success('Invite sent')
    onInvited()
  }

  return (
    <form onSubmit={onSubmit}
      style={{ background: 'var(--surface)', border: '1px solid var(--accent-border)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <input value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="First name *"
          style={inp} />
        <input value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Last name"
          style={inp} />
      </div>
      <input value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@company.com *" type="email"
        style={inp} />
      <div>
        <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Role</label>
        <select value={form.role} onChange={e => set('role', e.target.value as Role)} style={{ ...inp, cursor: 'pointer' }}>
          <option value="admin">Admin — full access</option>
          <option value="member">Member — approve under threshold</option>
          <option value="viewer">Viewer — jobs only, no money surfaces</option>
        </select>
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>
          <AlertCircle style={{ width: 10, height: 10, display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
          {ROLE_COPY[form.role].one_liner}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onClose} disabled={busy}
          style={{ flex: 1, padding: '9px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Cancel
        </button>
        <button type="submit" disabled={busy}
          style={{ flex: 1, padding: '9px', background: 'var(--accent)', border: 'none', borderRadius: 7, color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {busy ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 12, height: 12 }} />}
          {busy ? 'Sending…' : 'Send invite'}
        </button>
      </div>
    </form>
  )
}

function ThresholdCard({ threshold, onSaved }: { threshold: number | null; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState<string>(threshold !== null ? String(threshold) : '')
  const [busy, setBusy] = useState(false)
  const { portalUser } = useAuth()

  const save = async (newVal: number | null) => {
    setBusy(true)
    const { data, error } = await supabase.functions.invoke<any>('portal-invite', {
      body: { action: 'set_threshold', threshold: newVal },
    })
    setBusy(false)
    if (error || data?.error) {
      toast.error(data?.error || error?.message || 'Failed to set threshold')
      return
    }
    toast.success(newVal === null ? 'Threshold cleared' : `Threshold set to ${formatCurrency(newVal, 'USD')}`)
    onSaved()
    setEditing(false)
  }

  if (!editing) {
    return (
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Approval threshold</p>
            <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.01em' }}>
              {threshold !== null ? formatCurrency(threshold, 'USD') : 'Not set'}
            </p>
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '4px 0 0', lineHeight: 1.5 }}>
              {threshold !== null
                ? `Quotes above ${formatCurrency(threshold, 'USD')} require admin approval. Members can recommend.`
                : 'Any member can approve at any total. Set a limit to require admin approval above it.'}
            </p>
          </div>
          <button type="button" onClick={() => { setValue(threshold !== null ? String(threshold) : ''); setEditing(true) }}
            disabled={!portalUser}
            style={{ padding: '7px 12px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            {threshold !== null ? 'Change' : 'Set'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--accent-border)', borderRadius: 12, padding: '14px 16px' }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>Approval threshold</p>
      <input type="number" min="0" step="0.01" value={value} onChange={e => setValue(e.target.value)} placeholder="e.g. 500"
        autoFocus
        style={{ ...inp, marginBottom: 8 }} />
      <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '0 0 10px', lineHeight: 1.5 }}>
        Leave empty to let any member approve at any total.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => setEditing(false)} disabled={busy}
          style={{ flex: 1, padding: '8px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Cancel
        </button>
        <button type="button" onClick={() => save(null)} disabled={busy}
          style={{ flex: 1, padding: '8px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Clear
        </button>
        <button type="button" onClick={() => save(value.trim() === '' ? null : Number(value))} disabled={busy}
          style={{ flex: 1, padding: '8px', background: 'var(--accent)', border: 'none', borderRadius: 7, color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {busy ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Save style={{ width: 12, height: 12 }} />}
          Save
        </button>
      </div>
    </div>
  )
}

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 11px',
  background: 'var(--field-bg)',
  border: '1px solid var(--border-default)',
  borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)',
  outline: 'none', boxSizing: 'border-box',
}

